'use client';

/**
 * Shared horizontal rail / slider primitive. Used wherever the portal
 * scrolls cards horizontally (CourseRow, video rail, top10, etc.).
 *
 * <p>What it ships:
 * <ul>
 *   <li>Native horizontal scroll with hidden scrollbar (all engines).</li>
 *   <li>Subtle floating prev/next pills that hide on touch and on
 *       hover-less devices.</li>
 *   <li>CSS scroll-snap (start / center / none).</li>
 *   <li>Keyboard nav: Arrow keys scroll one page, Home/End jump to ends.</li>
 *   <li>Per-instance CSS scope via {@code useId} so multiple rails
 *       don't collide.</li>
 * </ul>
 *
 * <p>What it does NOT ship: pagination dots, autoplay, vertical
 * scrolling — use a different primitive for those.
 */
import {
  Children,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { useBlockPart } from '@akademi/site-builder-runtime';
import { muted, sectionTitle } from '../_shared/styles';

export type ContentRailSnap = 'start' | 'center' | 'none';
export type ContentRailArrows = 'always' | 'hover' | 'auto' | 'none';
/**
 * How the user drives the rail horizontally.
 * - {@code 'drag'} — free-scrolling: grab and pull the row with the
 *   mouse (scroll-snap relaxed so it glides). Arrows follow {@code arrows}.
 * - {@code 'buttons'} — no drag; advance only via the always-visible
 *   prev/next pills, like a stepper / learning-journey.
 * - {@code 'scrollbar'} — native horizontal scroll with a visible
 *   scrollbar at the bottom; the mouse wheel / trackpad scrolls the row
 *   sideways. No drag, no overlay arrows — the scrollbar is the affordance.
 * - {@code 'wheel'} — the same wheel-driven horizontal scroll as
 *   {@code 'scrollbar'} but with the scrollbar hidden: hover the rail and
 *   spin the wheel down to move right. Overlay arrows follow {@code arrows}.
 * When omitted, the rail keeps its legacy behaviour (native scroll,
 * {@code arrows}-driven pills, no drag).
 */
export type ContentRailScrollMode = 'drag' | 'buttons' | 'scrollbar' | 'wheel';

export interface ContentRailProps {
  children: ReactNode;
  /** Definitive width each item takes. {@code 'auto'} keeps the
   *  content-driven path; a length like {@code '300px'} or a clamp
   *  sizes the wrapper to exactly that value. Defaults to
   *  {@code var(--rail-item-width, 260px)}. */
  itemWidth?: string;
  gap?: string;
  snap?: ContentRailSnap;
  /** Leaves a right-side padding so the next item peeks in, signaling
   *  more content. Recommended unless the rail is known to fit in one
   *  go. Default {@code true}. */
  peek?: boolean;
  /** Fades the leading/trailing edges of the scroller with a mask
   *  gradient, so an item that doesn't fully fit softens into the
   *  background instead of being sharply clipped. Default {@code false}. */
  edgeFade?: boolean;
  arrows?: ContentRailArrows;
  /** Interaction model — see {@link ContentRailScrollMode}. When unset,
   *  the rail keeps its legacy behaviour (native scroll + {@code arrows}
   *  pills, no drag). */
  scrollMode?: ContentRailScrollMode;
  /** Section heading rendered inside the rail's own header row (title on
   *  the inline-start, arrows on the inline-end, {@code space-between}) —
   *  matching the LearningJourneys / TestimonialsBento pattern. Only used
   *  in {@code 'buttons'} mode; in that mode the caller should suppress
   *  its own section title to avoid a duplicate. */
  heading?: ReactNode;
  /** Overrides merged over the default section-title styling, for hosts
   *  whose own headings don't use block typography (e.g. the LMS portal's
   *  {@code Typography} scale) and would otherwise get a title two sizes
   *  bigger than every other heading on the page. */
  headingStyle?: CSSProperties;
  /** Optional sub-heading shown under {@link heading}. */
  subheading?: ReactNode;
  /** Optional extra control (e.g. a "See all" link) placed just before
   *  the prev/next arrows on the inline-end of the header row. */
  headerAction?: ReactNode;
  /** Required — the rail is a {@code role="region"}, screen readers
   *  pronounce labeled regions distinctly. Pass the rail's heading. */
  ariaLabel: string;
  /** Accessible names for the prev/next arrows. Default to English — hosts
   *  that have a translation catalog should pass their own. */
  prevLabel?: string;
  nextLabel?: string;
  padding?: string;
  className?: string;
  style?: CSSProperties;
  /** Called (repeatedly, as needed) once the scroller has been dragged/
   *  scrolled/paged to within half a viewport of its trailing edge —
   *  drives "load more" pagination without the caller wiring its own
   *  scroll listener. Only fires while there's overflow to scroll
   *  through; harmless to pass unconditionally. */
  onReachEnd?: () => void;
  /** When true, the next-arrow (header or overlay) swaps its chevron for a
   *  spinner — visual feedback that {@link onReachEnd}'s pagination fetch
   *  is in flight, so the arrow doesn't look inert while more content
   *  loads. */
  loadingMore?: boolean;
  /** Page dots under the rail — one per viewport-width page, the active
   *  one following the scroll position, each a button that scrolls to its
   *  page. Rendered only when the rail actually overflows. */
  dots?: boolean;
  /** Accessible name for a dot, e.g. "Page 2 of 5". English default. */
  dotLabel?: (index: number, count: number) => string;
}

export function ContentRail({
  children,
  itemWidth = 'var(--rail-item-width, 260px)',
  gap = 'var(--space-4, 1rem)',
  snap = 'start',
  peek = true,
  edgeFade = false,
  arrows = 'auto',
  scrollMode,
  heading,
  headingStyle,
  subheading,
  headerAction,
  ariaLabel,
  prevLabel = 'Scroll left',
  nextLabel = 'Scroll right',
  padding,
  className,
  style,
  onReachEnd,
  loadingMore,
  dots = false,
  dotLabel = (i, n) => `Page ${i + 1} of ${n}`,
}: ContentRailProps) {
  const rawId = useId();
  const scope = `cr${rawId.replace(/[^a-zA-Z0-9]/g, '')}`;
  // data-<ns>-part markers on the shared rail structure — one edit covers
  // every rail block that renders through ContentRail. See blockIdentity.
  const part = useBlockPart();

  // 'buttons' = paginated stepper: arrows move into the header row (not
  // overlaid on the rail) and drag is off; 'drag' enables grab-to-scroll
  // and relaxes snap so the row glides freely. Unset = legacy behaviour
  // (native scroll, overlay arrows per `arrows`, no drag).
  const dragEnabled = scrollMode === 'drag';
  const arrowsInHeader = scrollMode === 'buttons';
  // 'scrollbar' = native horizontal scroll with the scrollbar left visible
  // at the bottom; the wheel scrolls sideways and no arrows are drawn.
  const showScrollbar = scrollMode === 'scrollbar';
  // Both 'scrollbar' and 'wheel' translate a vertical wheel gesture into
  // horizontal movement; 'wheel' just hides the scrollbar.
  const wheelHorizontal = scrollMode === 'scrollbar' || scrollMode === 'wheel';
  // Overlay (floating) arrows — suppressed when the arrows live in the header
  // or when the visible scrollbar is the affordance.
  const overlayArrows: ContentRailArrows = arrowsInHeader || showScrollbar ? 'none' : arrows;
  const effectiveSnap: ContentRailSnap = dragEnabled || wheelHorizontal ? 'none' : snap;

  const scrollerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  // Page geometry for the dots: how many viewport-widths the row spans and
  // which one the scroll position is closest to.
  const [pageCount, setPageCount] = useState(1);
  const [activePage, setActivePage] = useState(0);
  // Tracks an in-flight pointer drag. `moved` gates the click-suppression
  // so a genuine tap on a card still navigates.
  const dragRef = useRef({ active: false, startX: 0, startScroll: 0, moved: false });

  // Kept in a ref (rather than a updateScrollState dependency) so the
  // scroll/resize listeners below don't need to be torn down and
  // re-attached every time the caller passes a new onReachEnd closure.
  const onReachEndRef = useRef(onReachEnd);
  useEffect(() => {
    onReachEndRef.current = onReachEnd;
  });

  const updateScrollState = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const atStart = el.scrollLeft <= 1;
    const remaining = el.scrollWidth - el.clientWidth - el.scrollLeft;
    const atEnd = remaining <= 1;
    setCanScrollLeft(!atStart);
    setCanScrollRight(!atEnd);
    if (el.clientWidth > 0) {
      const pages = Math.max(1, Math.ceil((el.scrollWidth - 1) / el.clientWidth));
      setPageCount(pages);
      // The true last scroll position (`scrollWidth - clientWidth`) is
      // rarely an exact multiple of `clientWidth` — rounding it can land
      // one dot short of the end. Pin the dot to the real boundary
      // whenever the scroller is actually there (same `atStart`/`atEnd`
      // this function already computed), and only round for positions in
      // between.
      const page = atEnd
        ? pages - 1
        : atStart
          ? 0
          : Math.min(pages - 1, Math.round(el.scrollLeft / el.clientWidth));
      setActivePage(page);
    }
    // Fires whenever the scroller is within half a viewport of its end —
    // including a row that doesn't overflow its container at all yet
    // (remaining <= 0), so a short first page can immediately top itself
    // up. The caller (CourseRow) is responsible for de-duping concurrent
    // calls and stopping once the feed is exhausted.
    if (remaining <= el.clientWidth * 0.5) {
      onReachEndRef.current?.();
    }
  }, []);

  useEffect(() => {
    updateScrollState();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollState, { passive: true });
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      ro.disconnect();
    };
  }, [updateScrollState, children]);

  // 'scrollbar' mode: translate a predominantly-vertical wheel gesture into
  // horizontal movement so the mouse wheel scrolls the row sideways. Only
  // hijack the wheel while there is somewhere horizontal to go, so the page
  // still scrolls normally once the rail hits its end.
  useEffect(() => {
    if (!wheelHorizontal) return;
    const el = scrollerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      if (el.scrollWidth <= el.clientWidth) return;
      const atStart = el.scrollLeft <= 0;
      const atEnd = el.scrollLeft >= el.scrollWidth - el.clientWidth - 1;
      if ((e.deltaY < 0 && atStart) || (e.deltaY > 0 && atEnd)) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [wheelHorizontal]);

  const scrollToPage = useCallback((index: number) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTo({ left: index * el.clientWidth, behavior: 'smooth' });
  }, []);

  const scrollByPage = useCallback((dir: 'left' | 'right') => {
    const el = scrollerRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.85;
    el.scrollBy({ left: dir === 'left' ? -amount : amount, behavior: 'smooth' });
  }, []);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        scrollByPage('left');
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        scrollByPage('right');
      } else if (e.key === 'Home') {
        e.preventDefault();
        scrollerRef.current?.scrollTo({ left: 0, behavior: 'smooth' });
      } else if (e.key === 'End') {
        e.preventDefault();
        const el = scrollerRef.current;
        if (el) el.scrollTo({ left: el.scrollWidth, behavior: 'smooth' });
      }
    },
    [scrollByPage],
  );

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!dragEnabled || e.button !== 0) return;
      const el = scrollerRef.current;
      if (!el) return;
      dragRef.current = {
        active: true,
        startX: e.clientX,
        startScroll: el.scrollLeft,
        moved: false,
      };
      setIsDragging(true);
    },
    [dragEnabled],
  );

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const st = dragRef.current;
    if (!st.active) return;
    const el = scrollerRef.current;
    if (!el) return;
    const dx = e.clientX - st.startX;
    if (!st.moved && Math.abs(dx) > 3) {
      st.moved = true;
      // Capture the pointer only once we're sure it's a drag, so a plain
      // click never gets swallowed.
      el.setPointerCapture?.(e.pointerId);
    }
    if (st.moved) el.scrollLeft = st.startScroll - dx;
  }, []);

  const endDrag = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const st = dragRef.current;
    if (!st.active) return;
    st.active = false;
    setIsDragging(false);
    const el = scrollerRef.current;
    // Only release if we actually captured (drags do; plain clicks don't) —
    // releasing an uncaptured pointer throws in some engines.
    if (el?.hasPointerCapture?.(e.pointerId)) el.releasePointerCapture(e.pointerId);
  }, []);

  // Swallow the click that fires at the end of a drag so cards (links)
  // don't navigate when the user was only scrolling. Reset the flag either
  // way so the next real click passes through.
  const onClickCapture = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (dragRef.current.moved) {
      e.preventDefault();
      e.stopPropagation();
    }
    dragRef.current.moved = false;
  }, []);

  // No overflow at all in either direction => a single page fits, so arrows
  // (header or overlay) have nothing to do and should not render.
  const hasOverflow = canScrollLeft || canScrollRight;

  const items = Children.toArray(children);

  // Only fade the edge that still has more content behind it — the very
  // first/last page shows its outer edge at full opacity (nothing to hint at).
  const edgeMask = edgeFade
    ? `linear-gradient(to right, ${canScrollLeft ? 'transparent, black 24px' : 'black'}, ${
        canScrollRight ? 'black calc(100% - 24px), transparent' : 'black'
      })`
    : '';

  const css = `
    .${scope}-scroller {
      overflow-x: auto;
      overflow-y: hidden;
      scroll-snap-type: ${effectiveSnap === 'none' ? 'none' : `x mandatory`};
      ${
        showScrollbar
          ? 'scrollbar-width: thin; scrollbar-color: var(--color-neutral-300, #d4d4d8) transparent;'
          : 'scrollbar-width: none; -ms-overflow-style: none;'
      }
      /* Wheel/scrollbar modes must track the wheel 1:1 — CSS smooth would
         restart an animation on every tick and feel laggy. Arrows/keyboard
         stay smooth because they pass an explicit behavior to scrollBy. */
      scroll-behavior: ${wheelHorizontal ? 'auto' : 'smooth'};
      display: flex;
      gap: ${gap};
      ${peek ? 'padding-inline-end: clamp(2rem, 8vw, 5rem);' : ''}
      /* overflow-y: hidden above clips to this padding box, so a card's
         hover lift/shadow needs real room here or it gets cut off — that's
         NOT a card bug, it's this padding being too tight (was 2px) for any
         card style with a hover-lift shadow (translateY + blur). The
         matching negative margin cancels the padding back out of the
         surrounding layout, so this rail still takes up exactly as much
         vertical space as before at rest — only the clip boundary moves.
         (Bottom keeps its extra 8px in scrollbar mode — that's a real
         visible scrollbar strip, not clip room, so it isn't canceled.) */
      padding-top: 24px;
      margin-top: -24px;
      padding-bottom: ${showScrollbar ? '32px' : '24px'};
      margin-bottom: -24px;
      ${dragEnabled ? 'cursor: grab;' : ''}
      ${
        edgeFade
          ? `
      mask-image: ${edgeMask};
      -webkit-mask-image: ${edgeMask};
      `
          : ''
      }
    }
    ${dragEnabled ? `.${scope}-scroller[data-dragging="true"] { cursor: grabbing; scroll-behavior: auto; user-select: none; }` : ''}
    ${
      showScrollbar
        ? `
      .${scope}-scroller::-webkit-scrollbar { height: 8px; }
      .${scope}-scroller::-webkit-scrollbar-track { background: transparent; }
      .${scope}-scroller::-webkit-scrollbar-thumb {
        background: var(--color-neutral-300, #d4d4d8);
        border-radius: 999px;
      }
      .${scope}-scroller::-webkit-scrollbar-thumb:hover {
        background: var(--color-neutral-400, #a1a1aa);
      }
    `
        : `.${scope}-scroller::-webkit-scrollbar { display: none; width: 0; height: 0; }`
    }
    .${scope}-item {
      flex: 0 0 auto;
      width: ${itemWidth};
      display: flex;
      flex-direction: column;
      scroll-snap-align: ${effectiveSnap === 'none' ? 'none' : effectiveSnap};
    }
    .${scope}-arrow {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      /* Below the hover-elevated cards (UdemyCourseCard / MarketplaceCourseCard
         raise themselves to z-index 30 while their hover popover is open, and
         that z-index makes the card a stacking context, so the popover's own
         z-index 9999 counts as 30 out here). An open popover should fully
         cover the arrows underneath it rather than an arrow circle floating
         on top of the popover's content. Still above ordinary (non-hovered)
         cards, so the arrows stay visible/clickable the rest of the time. */
      z-index: var(--rail-arrow-z, 20);
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: var(--rail-arrow-size, 40px);
      height: var(--rail-arrow-size, 40px);
      border-radius: 999px;
      border: 1px solid var(--color-border-subtle, rgba(0, 0, 0, 0.08));
      background: var(--color-surface-card, #ffffff);
      color: var(--color-text-primary, #0f172a);
      cursor: pointer;
      box-shadow: var(--rail-arrow-shadow, 0 6px 18px rgba(15, 23, 42, 0.12));
      transition: opacity 160ms ease, transform 160ms ease, background 160ms ease;
      padding: 0;
      font: inherit;
    }
    .${scope}-arrow:hover {
      transform: translateY(-50%) scale(1.05);
      background: var(--color-surface-hover, var(--color-neutral-50, #f8fafc));
    }
    .${scope}-arrow:focus-visible {
      outline: 2px solid var(--color-brand-500, #6366f1);
      outline-offset: 2px;
    }
    .${scope}-arrow--prev { left: 0; }
    .${scope}-arrow--next { right: 0; }
    ${
      overlayArrows === 'hover' || overlayArrows === 'auto'
        ? `
      .${scope}-root .${scope}-arrow { opacity: 0; }
      @media (hover: hover) {
        .${scope}-root:hover .${scope}-arrow,
        .${scope}-root:focus-within .${scope}-arrow { opacity: 1; }
      }
    `
        : ''
    }
    ${
      overlayArrows === 'auto'
        ? `
      @media (pointer: coarse) {
        .${scope}-arrow { display: none; }
      }
    `
        : ''
    }
    .${scope}-root .${scope}-arrow[data-disabled="true"] {
      opacity: 0;
      pointer-events: none;
    }
    /* Header row (buttons/paginated mode): title inline-start, arrows inline-end. */
    .${scope}-header {
      display: flex;
      align-items: center;
      gap: 16px;
      margin-bottom: var(--space-6, 24px);
    }
    .${scope}-hnav {
      margin-inline-start: auto;
      display: inline-flex;
      align-items: center;
      gap: var(--space-2, 8px);
      flex: 0 0 auto;
      /* Header arrows sit just above the first row of cards, and a card's
         hover popover opens 12px above the card — same clash as the overlay
         arrows, so lift these out of its way too. */
      position: relative;
      z-index: var(--rail-arrow-z, 20);
    }
    .${scope}-harrow {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: var(--rail-arrow-size, 40px);
      height: var(--rail-arrow-size, 40px);
      border-radius: 999px;
      border: 1px solid var(--color-border-subtle, rgba(0, 0, 0, 0.08));
      background: var(--color-surface-card, #ffffff);
      color: var(--color-text-primary, #0f172a);
      cursor: pointer;
      transition: transform 160ms ease, background 160ms ease, opacity 160ms ease;
      padding: 0;
      font: inherit;
    }
    .${scope}-harrow:hover { transform: scale(1.05); background: var(--color-surface-hover, var(--color-neutral-50, #f8fafc)); }
    .${scope}-harrow:focus-visible { outline: 2px solid var(--color-brand-500, #6366f1); outline-offset: 2px; }
    .${scope}-harrow[data-disabled="true"] { opacity: 0.4; pointer-events: none; }
    @keyframes content-rail-spin { to { transform: rotate(360deg); } }
    .${scope}-dots {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      margin-top: var(--space-5, 20px);
    }
    .${scope}-dot {
      width: 8px;
      height: 8px;
      padding: 0;
      border: 0;
      border-radius: 999px;
      background: var(--color-border, rgba(0, 0, 0, 0.15));
      cursor: pointer;
      transition: transform 160ms ease, background 160ms ease, width 160ms ease;
    }
    .${scope}-dot:hover { background: var(--color-brand-300, #93c5fd); }
    .${scope}-dot[aria-current="true"] {
      width: 22px;
      background: var(--color-brand-500, #3b5bdb);
    }
    .${scope}-dot:focus-visible { outline: 2px solid var(--color-brand-500, #3b5bdb); outline-offset: 2px; }
  `;

  return (
    <div
      role="region"
      aria-label={ariaLabel}
      onKeyDown={onKeyDown}
      tabIndex={0}
      className={`${scope}-root${className ? ' ' + className : ''}`}
      style={{
        position: 'relative',
        padding,
        outline: 'none',
        ...style,
      }}
    >
      <style dangerouslySetInnerHTML={{ __html: css }} />

      {arrowsInHeader && (
        <div className={`${scope}-header`}>
          {heading != null && heading !== '' && (
            <div>
              <h2
                {...part('title')}
                style={headingStyle ? { ...sectionTitle, ...headingStyle } : sectionTitle}
              >
                {heading}
              </h2>
              {subheading != null && subheading !== '' && (
                <div {...part('subtitle')} style={{ ...muted, marginTop: 4 }}>
                  {subheading}
                </div>
              )}
            </div>
          )}
          <div className={`${scope}-hnav`}>
            {headerAction}
            {hasOverflow && (
              <>
                <button
                  type="button"
                  aria-label={prevLabel}
                  className={`${scope}-harrow`}
                  data-disabled={!canScrollLeft}
                  onClick={() => scrollByPage('left')}
                >
                  <ChevronLeftIcon />
                </button>
                <button
                  type="button"
                  aria-label={loadingMore ? 'Loading more' : nextLabel}
                  className={`${scope}-harrow`}
                  data-disabled={!canScrollRight}
                  onClick={() => scrollByPage('right')}
                >
                  {loadingMore ? <SpinnerIcon /> : <ChevronRightIcon />}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <div
        ref={scrollerRef}
        {...part('list')}
        className={`${scope}-scroller`}
        data-dragging={isDragging ? 'true' : undefined}
        onPointerDown={dragEnabled ? onPointerDown : undefined}
        onPointerMove={dragEnabled ? onPointerMove : undefined}
        onPointerUp={dragEnabled ? endDrag : undefined}
        onPointerCancel={dragEnabled ? endDrag : undefined}
        onClickCapture={dragEnabled ? onClickCapture : undefined}
      >
        {items.map((child, i) => (
          <div key={i} {...part('item', i)} className={`${scope}-item`}>
            {child}
          </div>
        ))}
      </div>

      {dots && hasOverflow && pageCount > 1 && (
        <div {...part('dots')} className={`${scope}-dots`}>
          {Array.from({ length: pageCount }).map((_, i) => (
            <button
              key={i}
              type="button"
              {...part('dot', i)}
              className={`${scope}-dot`}
              aria-label={dotLabel(i, pageCount)}
              aria-current={i === activePage ? 'true' : undefined}
              onClick={() => scrollToPage(i)}
            />
          ))}
        </div>
      )}

      {overlayArrows !== 'none' && hasOverflow && (
        <>
          <button
            type="button"
            aria-label={prevLabel}
            className={`${scope}-arrow ${scope}-arrow--prev`}
            data-disabled={!canScrollLeft}
            onClick={() => scrollByPage('left')}
            tabIndex={-1}
          >
            <ChevronLeftIcon />
          </button>
          <button
            type="button"
            aria-label={loadingMore ? 'Loading more' : nextLabel}
            className={`${scope}-arrow ${scope}-arrow--next`}
            data-disabled={!canScrollRight}
            onClick={() => scrollByPage('right')}
            tabIndex={-1}
          >
            {loadingMore ? <SpinnerIcon /> : <ChevronRightIcon />}
          </button>
        </>
      )}
    </div>
  );
}

function ChevronLeftIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function SpinnerIcon() {
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-block',
        width: 16,
        height: 16,
        borderRadius: '50%',
        border: '2px solid currentColor',
        borderTopColor: 'transparent',
        opacity: 0.7,
        animation: 'content-rail-spin 700ms linear infinite',
      }}
    />
  );
}
